import { useViewerPdfDocument, type PdfRenderTask } from '@ironview/interactive-viewer';
import { FileText, Layers3, Loader2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import type { SafetyLayout } from './types';

const PdfLayoutThumbnail = ({ layout }: { layout: SafetyLayout }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const renderTaskRef = useRef<PdfRenderTask | null>(null);
  const [renderFailed, setRenderFailed] = useState(false);
  const [hasRendered, setHasRendered] = useState(false);
  const documentQuery = useViewerPdfDocument(layout.pdf_file_url);

  useEffect(() => {
    setRenderFailed(false);
    setHasRendered(false);
  }, [layout.pdf_file_url, layout.pdf_page_number, layout.pdf_rotation]);

  useEffect(() => {
    const pdfDocument = documentQuery.data;
    const canvas = canvasRef.current;
    if (!pdfDocument || !canvas) return;

    let cancelled = false;

    const renderPage = async () => {
      try {
        const pageNumber = Math.min(Math.max(layout.pdf_page_number ?? 1, 1), pdfDocument.numPages);
        const page = await pdfDocument.getPage(pageNumber);
        if (cancelled) return;

        const rotation = [0, 90, 180, 270].includes(layout.pdf_rotation ?? 0)
          ? (layout.pdf_rotation ?? 0)
          : 0;
        const baseViewport = page.getViewport({ scale: 1, rotation });
        const pixelRatio = Math.min(window.devicePixelRatio || 1, 2);
        const scale = (720 / Math.max(baseViewport.width, baseViewport.height)) * pixelRatio;
        const viewport = page.getViewport({ scale, rotation });
        const context = canvas.getContext('2d');
        if (!context) throw new Error('Canvas rendering is unavailable');

        canvas.width = Math.max(1, Math.round(viewport.width));
        canvas.height = Math.max(1, Math.round(viewport.height));
        renderTaskRef.current?.cancel();
        const renderTask = page.render({ canvasContext: context, viewport });
        renderTaskRef.current = renderTask;
        await renderTask.promise;

        if (!cancelled) {
          setHasRendered(true);
          page.cleanup?.();
        }
      } catch (error) {
        if (
          !cancelled &&
          (!(error instanceof Error) || error.name !== 'RenderingCancelledException')
        ) {
          setRenderFailed(true);
        }
      }
    };

    void renderPage();
    return () => {
      cancelled = true;
      renderTaskRef.current?.cancel();
    };
  }, [documentQuery.data, layout.pdf_page_number, layout.pdf_rotation]);

  if (documentQuery.isError || renderFailed) {
    return (
      <span
        role="img"
        aria-label={`${layout.name} PDF layout`}
        className="grid size-full place-items-center bg-[radial-gradient(circle_at_top_right,hsl(var(--primary)/0.14),transparent_60%)]"
      >
        <FileText className="size-11 text-muted-foreground" strokeWidth={1.5} />
      </span>
    );
  }

  return (
    <span
      role="img"
      aria-label={`${layout.name} PDF layout`}
      className="relative grid size-full place-items-center overflow-hidden bg-white"
    >
      <canvas
        ref={canvasRef}
        aria-hidden="true"
        className={`size-full object-contain transition-opacity ${hasRendered ? 'opacity-100' : 'opacity-0'}`}
        style={{
          transform: `scale(${layout.flipped_horizontal ? -1 : 1}, ${layout.flipped_vertical ? -1 : 1})`,
        }}
      />
      {!hasRendered && (
        <Loader2 className="absolute size-6 animate-spin text-primary" aria-hidden="true" />
      )}
    </span>
  );
};

export const SafetyLayoutThumbnail = ({ layout }: { layout: SafetyLayout }) => {
  const [imageFailed, setImageFailed] = useState(false);
  const imageUrl = layout.image_url ?? layout.pdf_preview_url;

  useEffect(() => setImageFailed(false), [imageUrl]);

  if (imageUrl && !imageFailed) {
    return (
      <img
        src={imageUrl}
        alt={`${layout.name} layout`}
        onError={() => setImageFailed(true)}
        className="size-full object-contain transition duration-200 group-hover:scale-[1.015]"
      />
    );
  }

  if (layout.pdf_file_url) return <PdfLayoutThumbnail layout={layout} />;

  return (
    <span className="grid size-full place-items-center bg-[radial-gradient(circle_at_top_right,hsl(var(--primary)/0.14),transparent_60%)]">
      <Layers3 className="size-11 text-muted-foreground" strokeWidth={1.5} />
    </span>
  );
};
